import { useEffect, useState } from 'react';
import { useMoveOperate } from '../../hooks/use-move-operate';
import { useScaleOperate } from '../../hooks/use-scale-operate';
import { useSizeChangeOperate } from '../../hooks/use-size-change';
import { useStageCropStore } from '../../models/crop-stage-state';
import CropGrid from './grid';
import MoveAction from './move-action';
import ScaleAction from './scale-action';
import SizeAction from './size-action';
import './operate-stage.less';
import IconFont from '@meitu/xdesign-icon-font';
import { IFramework } from '@/framework';
import { getLocaleStore } from '@/i18n';
import { useRotateOperate } from '../../hooks/use-rotate-operate';
import { CROP_STAGE_EVENT } from '../../types';
import { getTranslateToStageCenter } from '../../utils';
import Mask from './mask';
import RotateAction from './rotate-action';

interface IOperateStageProps {
    stageWidth: number;
    stageHeight: number;
    scale: number;
    maskURL: string;
    isResize: number;
    framework: IFramework;
}

const getStageScaleStyle = (stageWidth: number, stageHeight: number, scale: number) => {
    const element = document.getElementById('operate-stage-wrap');
    let translatePoint = {
        x: 0,
        y: 0,
    };
    if (element) {
        translatePoint = getTranslateToStageCenter(stageWidth * scale, stageHeight * scale);
    }

    const style = {
        width: stageWidth * scale,
        height: stageHeight * scale,
        position: 'relative',
        transform: `translate(${-translatePoint.x}px, ${-translatePoint.y}px)`,
        boxShadow: `0 0 0 1px var(--stroke-cursor-target-color),0 0 0 ${10000}px rgba(255,255,255,0.5)`,
    } as React.CSSProperties;

    return style;
};

const OperateStage: React.FC<IOperateStageProps> = ({
    scale,
    stageWidth,
    stageHeight,
    maskURL,
    isResize,
    framework,
}) => {
    const { $t } = getLocaleStore();
    const { isCropStageOperating } = useStageCropStore()((state) => state);

    const [operateStageStyle, setOperateStageStyle] = useState<React.CSSProperties>({});

    useMoveOperate(scale);

    useScaleOperate(scale);
    useSizeChangeOperate(scale);

    useRotateOperate();

    useEffect(() => {
        if (isCropStageOperating) {
            return;
        }
        const style = getStageScaleStyle(stageWidth, stageHeight, scale);
        setOperateStageStyle(style);
    }, [isCropStageOperating, scale, stageHeight, stageWidth, isResize]);

    useEffect(() => {
        const editorContainer = document.getElementById('editor-container');
        const handleClick = (e: MouseEvent) => {
            const cropImgPluginWrap = document.getElementById('crop-stage-wrap');
            if (
                !(cropImgPluginWrap?.getElementsByClassName('base-operate')?.[0] as HTMLDivElement)?.contains(
                    e.target as Node,
                ) &&
                !document.getElementById('operate-stage-wrap')?.contains(e.target as Node)
            ) {
                framework.events.emit(CROP_STAGE_EVENT.CONFIRM);
            }
        };

        editorContainer?.addEventListener('mousedown', handleClick);
        return () => {
            editorContainer?.removeEventListener('mousedown', handleClick);
        };
    }, []);

    return (
        <div className="l-operate-stage">
            <div style={operateStageStyle} id="operate-stage-wrap">
                {!isCropStageOperating && (
                    <div className="l-operate-stage_confirm">
                        <div
                            className="btn-cancel"
                            onClick={() => {
                                framework.events.emit(CROP_STAGE_EVENT.CANCEL);
                            }}
                        >
                            <IconFont name="CrossCircle" fontSize={16} color="var(--content-system-primary, #1C1D1F)" />
                            <div>{$t((v) => v.collapse.adjustCollapse.cancelText)}</div>
                        </div>
                        <div
                            className="btn-confirm"
                            onClick={() => {
                                framework.events.emit(CROP_STAGE_EVENT.CONFIRM);
                            }}
                        >
                            <IconFont name="CheckCircle" fontSize={16} color="var(--content-tag)" />
                            <div>{$t((v) => v.collapse.adjustCollapse.okText)}</div>
                        </div>
                    </div>
                )}
                <Mask
                    maskURL={maskURL}
                    stageWidth={stageWidth}
                    stageHeight={stageHeight}
                    isOperating={isCropStageOperating}
                    scale={scale}
                />
                <MoveAction width={stageWidth * scale} height={stageHeight * scale} />

                <SizeAction />

                <ScaleAction />

                <CropGrid />

                <RotateAction isOperating={isCropStageOperating} />
            </div>
        </div>
    );
};

export default OperateStage;
